import React, { useState, useEffect } from 'react';
import { Search, User, Calendar, AlertCircle, CheckCircle, List, RefreshCw, Wifi } from 'lucide-react';
import myobFirebaseService from 'services/myobFirebaseService';
import { useAuthContext } from 'context/auth-context';
import toast from 'react-hot-toast';

const MYOBLeaveBalanceTester = () => {
    const { currentUserData } = useAuthContext();
    const [displayId, setDisplayId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [tokens, setTokens] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [showEmployeeList, setShowEmployeeList] = useState(false);
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [connectionTestResult, setConnectionTestResult] = useState(null);
    const [isUpdatingToken, setIsUpdatingToken] = useState(false);

    useEffect(() => {
        if (currentUserData?.id) {
            myobFirebaseService.setCurrentUser(currentUserData.id);
            checkAuthentication();
        }
    }, [currentUserData]);

    const checkAuthentication = async () => {
        try {
            const storedTokens = await myobFirebaseService.loadTokensFromFirebase();
            setTokens(storedTokens);
            setIsAuthenticated(!!storedTokens?.access_token);
        } catch (error) {
            console.error('Error checking authentication:', error);
            setIsAuthenticated(false);
        }
    };

    const loadEmployees = async () => {
        console.log('🚀 Load Employees button clicked');
        console.log('isAuthenticated:', isAuthenticated);
        console.log('tokens:', tokens);
        
        if (!isAuthenticated || !tokens) {
            console.log('❌ Not authenticated or no tokens');
            toast.error('Please authenticate with MYOB first');
            return;
        }

        setIsLoadingEmployees(true);
        setError(null);

        try {
            console.log('👥 Loading employees with leave data...');
            console.log('myobFirebaseService:', myobFirebaseService);
            
            // Ensure we have business ID
            console.log('🔍 Getting business ID...');
            const businessId = await myobFirebaseService.ensureBusinessId();
            console.log('✅ Using business ID:', businessId);
            
            if (!businessId) {
                throw new Error('Unable to get business ID. Please re-authenticate with MYOB.');
            }
            
            // Get all employees with leave data using the working endpoint
            console.log('📡 Calling getAllEmployeesWithLeaveData...');
            const employeeList = await myobFirebaseService.getAllEmployeesWithLeaveData(businessId);
            console.log('✅ Got employee list:', employeeList.length, 'employees');
            
            setEmployees(employeeList);
            setShowEmployeeList(true);
            toast.success(`Loaded ${employeeList.length} employees with leave data`);
        } catch (err) {
            console.error('❌ Error loading employees:', err);
            console.error('Error stack:', err.stack);
            setError(err.message);
            
            // Provide more specific error messages
            if (err.message.includes('fetch')) {
                toast.error('Network error: Unable to connect to MYOB. Please check your connection and try again.');
            } else if (err.message.includes('business ID')) {
                toast.error('Business ID error: Please re-authenticate with MYOB.');
            } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
                toast.error('Authentication error: Please re-authenticate with MYOB.');
            } else {
                toast.error(`Error loading employees: ${err.message}`);
            }
        } finally {
            setIsLoadingEmployees(false);
        }
    };

    const selectEmployee = (employee) => {
        setDisplayId(employee.displayId || '*None');
        setShowEmployeeList(false);
        toast.success(`Selected employee: ${employee.name}`);
    };

    const testConnection = async () => {
        console.log('🚀 Test Connection button clicked');
        console.log('isAuthenticated:', isAuthenticated);
        console.log('tokens:', tokens);
        
        if (!isAuthenticated || !tokens) {
            console.log('❌ Not authenticated or no tokens');
            toast.error('Please authenticate with MYOB first');
            return;
        }

        setIsTestingConnection(true);
        setConnectionTestResult(null);
        setError(null);

        try {
            console.log('🔍 Testing MYOB connection...');
            console.log('myobFirebaseService:', myobFirebaseService);
            const result = await myobFirebaseService.testConnection();
            console.log('✅ Test connection result:', result);
            setConnectionTestResult(result);
            
            if (result.success) {
                toast.success('Connection test successful!');
            } else {
                toast.error(`Connection test failed: ${result.error}`);
            }
        } catch (err) {
            console.error('❌ Error testing connection:', err);
            console.error('Error stack:', err.stack);
            setError(err.message);
            toast.error(`Connection test error: ${err.message}`);
        } finally {
            setIsTestingConnection(false);
        }
    };

    const updateRefreshToken = async () => {
        if (!isAuthenticated) {
            toast.error('Please authenticate with MYOB first');
            return;
        }

        setIsUpdatingToken(true);
        setError(null);

        try {
            console.log('🔄 Updating refresh token...');
            const newRefreshToken = 'ory_rt_NGI3!AAAAAOGq31sfoQGvwNEIydI9UM9bT4zSI7D_MG5FL2_LUgq_AAAAAAIVZYUpJc3RAwLEZS29kSD7Sj10wfsTI3T9HsjOqevNfCOKoDLP99Jg6lJ21KNju7CfEBFg4984zWUAcQ4Vhcj5oQqpZTn1w_Ew59plaJcElUJX8hu52YGPjQk5owojiWPDy5AIMCAYYRTm7iZxvY-xSCbV1S_rvj45A-Ue_6BvUnDr27TU-YRibCTCPIUDCn0MnDe87jZ5KiX8N4Flg_5YNMOoSVERSAH-8UirXQZqcVPhfPhDEceyYe8LorZI9ryO640waHkgrtK1RvbT12WWce_PIbLWYV09hButDKLiWRZvWBj-dXu-JUn8X7kpBAdjiBaV8rek94_STSAxx2gRUVM8PMsPgMV3pFdmdUoc4Q';
            
            await myobFirebaseService.updateRefreshToken(newRefreshToken);
            toast.success('Refresh token updated successfully!');
            
            // Refresh authentication status
            await checkAuthentication();
        } catch (err) {
            console.error('Error updating refresh token:', err);
            setError(err.message);
            toast.error(`Error updating refresh token: ${err.message}`);
        } finally {
            setIsUpdatingToken(false);
        }
    };

    const handleSearch = async () => {
        if (!displayId.trim()) {
            toast.error('Please enter an employee DisplayID');
            return;
        }

        if (!isAuthenticated || !tokens) {
            toast.error('Please authenticate with MYOB first');
            return;
        }

        setIsLoading(true);
        setError(null);
        setResult(null);

        try {
            console.log('🔍 Testing leave balance retrieval...');
            console.log('Available tokens:', tokens);
            console.log('Business ID:', tokens.business_id);
            console.log('Display ID:', displayId);

            // Use the Firebase service to get leave balances (it will handle business ID automatically)
            const leaveData = await myobFirebaseService.getLeaveBalancesByDisplayId(
                null, // Let the service determine business ID
                displayId.trim()
            );

            setResult(leaveData);
            toast.success('Leave balances retrieved successfully!');
        } catch (err) {
            console.error('Error retrieving leave balances:', err);
            setError(err.message);
            toast.error(`Error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const formatLeaveBalance = (balance) => {
        if (typeof balance === 'object' && balance !== null) {
            return `${balance.Hours || 0} hours (${balance.Days || 0} days)`;
        }
        return `${balance || 0} hours`;
    };

    return (
        <div className="max-w-7xl mx-auto p-6" style={{ display: 'none' }}>
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <Calendar className="h-8 w-8 text-blue-600" />
                        <h1 className="text-2xl font-bold text-gray-900">Employee Leave Balances</h1>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={updateRefreshToken}
                            disabled={isUpdatingToken || !isAuthenticated}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <span>{isUpdatingToken ? 'Updating...' : 'Update Token'}</span>
                        </button>
                        <button
                            onClick={testConnection}
                            disabled={isTestingConnection || !isAuthenticated}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Wifi className="h-4 w-4" />
                            <span>{isTestingConnection ? 'Testing...' : 'Test Connection'}</span>
                        </button>
                        <button
                            onClick={loadEmployees}
                            disabled={isLoadingEmployees || !isAuthenticated}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <List className="h-4 w-4" />
                            <span>{isLoadingEmployees ? 'Loading...' : 'Load Employees'}</span>
                        </button>
                    </div>
                </div>

                {!isAuthenticated && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                            <p className="text-yellow-800">Please authenticate with MYOB to view employee leave data.</p>
                        </div>
                    </div>
                )}

                {connectionTestResult && (
                    <div className={`mb-6 p-4 rounded-lg ${connectionTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center">
                            {connectionTestResult.success ? (
                                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                            ) : (
                                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            )}
                            <p className={connectionTestResult.success ? 'text-green-800' : 'text-red-800'}>
                                {connectionTestResult.success ? 'Connection successful!' : `Connection failed: ${connectionTestResult.error}`}
                            </p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            <p className="text-red-800">{error}</p>
                        </div>
                    </div>
                )}

                {employees.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Employees with Leave Data ({employees.length})
                            </h2>
                            <button
                                onClick={() => setShowEmployeeList(!showEmployeeList)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                {showEmployeeList ? 'Hide List' : 'Show List'}
                            </button>
                        </div>

                        {showEmployeeList && (
                            <div className="grid gap-4">
                                {employees.map((employee, index) => (
                                    <div
                                        key={employee.uid}
                                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3">
                                                    <User className="h-5 w-5 text-gray-400" />
                                                    <div>
                                                        <h3 className="font-medium text-gray-900">{employee.name}</h3>
                                                        <p className="text-sm text-gray-500">
                                                            {employee.isActive ? 'Active' : 'Inactive'} • 
                                                            {employee.annualSalary > 0 ? ` $${employee.annualSalary.toLocaleString()}/year` : ' No salary data'} • 
                                                            {employee.payFrequency}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-6">
                                                {employee.leaveBalances.annualLeave && (
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-500">Annual Leave</p>
                                                        <p className="text-lg font-semibold text-blue-600">
                                                            {employee.leaveBalances.annualLeave.totalHours.toFixed(2)} hours
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            Total: {employee.leaveBalances.annualLeave.total.toFixed(2)}h
                                                        </p>
                                                    </div>
                                                )}
                                                {employee.leaveBalances.longServiceLeave && (
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-500">Long Service</p>
                                                        <p className="text-lg font-semibold text-purple-600">
                                                            {employee.leaveBalances.longServiceLeave.totalHours.toFixed(2)} hours
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            Total: {employee.leaveBalances.longServiceLeave.total.toFixed(2)}h
                                                        </p>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => selectEmployee(employee)}
                                                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                                >
                                                    Select
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {employees.length === 0 && !isLoadingEmployees && isAuthenticated && (
                    <div className="text-center py-12">
                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No employees loaded</h3>
                        <p className="text-gray-500 mb-4">Click "Load Employees" to fetch employee leave data from MYOB.</p>
                        <button
                            onClick={loadEmployees}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Load Employees
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MYOBLeaveBalanceTester;
